import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RenderableActivityBlock } from "./activity-model";

type BlockExpansionConfig = {
  collapsible: boolean;
  defaultExpanded: boolean;
};

function getBlockExpansionConfig(renderableBlocks: RenderableActivityBlock[]) {
  const config = new Map<string, BlockExpansionConfig>();
  for (const renderableBlock of renderableBlocks) {
    const block = renderableBlock.block;
    config.set(block.id, {
      collapsible: block.collapsible,
      defaultExpanded: block.defaultExpanded,
    });
  }
  return config;
}

export function useActivityBlockExpansion(renderableBlocks: RenderableActivityBlock[]) {
  const [expandedBlocks, setExpandedBlocks] = useState<Record<string, boolean>>({});
  const userTouchedBlocksRef = useRef<Set<string>>(new Set());
  const blockExpansionConfig = useMemo(
    () => getBlockExpansionConfig(renderableBlocks),
    [renderableBlocks]
  );

  useEffect(() => {
    setExpandedBlocks((current) => {
      const next: Record<string, boolean> = {};
      for (const renderableBlock of renderableBlocks) {
        const block = renderableBlock.block;
        next[block.id] = userTouchedBlocksRef.current.has(block.id)
          ? (current[block.id] ?? block.defaultExpanded)
          : block.defaultExpanded;
      }
      return next;
    });
  }, [renderableBlocks]);

  const toggleBlock = useCallback(
    (blockId: string) => {
      const config = blockExpansionConfig.get(blockId);
      if (!config?.collapsible) {
        return;
      }

      userTouchedBlocksRef.current.add(blockId);
      setExpandedBlocks((current) => ({
        ...current,
        [blockId]: !(current[blockId] ?? config.defaultExpanded),
      }));
    },
    [blockExpansionConfig]
  );

  return { expandedBlocks, toggleBlock };
}
